"use client";

import { useState, useEffect, useRef, useMemo, createContext, useContext } from "react";
import { GLOSSARY } from "@/lib/glossary";

const THEME = {
  primary: "#0E5F56",
  sans: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
};

/* ─── Scope context ─── */
/* Each GlossaryScope tracks which tooltip definitions have already been shown.
   Within a scope, each term is only tooltipped on its first occurrence.
   Different scopes are independent — so the disease-info section and the
   trial-cards section each get their own first-occurrence tracking. */

const GlossaryScopeCtx = createContext<React.MutableRefObject<Set<string>> | null>(null);

export function GlossaryScope({ children }: { children: React.ReactNode }) {
  const shown = useRef<Set<string>>(new Set());
  return (
    <GlossaryScopeCtx.Provider value={shown}>
      {children}
    </GlossaryScopeCtx.Provider>
  );
}

/* ─── GlossaryTerm ─── */

export function GlossaryTerm({ text, tooltip }: { text: string; tooltip: string }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLSpanElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number; showAbove: boolean } | null>(null);
  const isTouchRef = useRef(false);

  useEffect(() => {
    if (!open) return;
    const handleOutside = (e: MouseEvent | TouchEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", handleOutside);
    document.addEventListener("touchstart", handleOutside);
    return () => {
      document.removeEventListener("mousedown", handleOutside);
      document.removeEventListener("touchstart", handleOutside);
    };
  }, [open]);

  useEffect(() => {
    if (open && ref.current) {
      const rect = ref.current.getBoundingClientRect();
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      const tooltipW = Math.min(260, vw - 16);
      let left = rect.left + rect.width / 2 - tooltipW / 2;
      if (left < 8) left = 8;
      if (left + tooltipW > vw - 8) left = vw - tooltipW - 8;
      // Show below if term is in the top 30% of viewport, otherwise show above
      const showAbove = rect.top > vh * 0.3;
      setPos({ top: rect.top, left, showAbove });
    }
  }, [open]);

  return (
    <span
      ref={ref}
      style={{ display: "inline" }}
    >
      <span
        onTouchStart={(e) => {
          isTouchRef.current = true;
          e.preventDefault();
          setOpen(prev => !prev);
        }}
        onClick={(e) => {
          if (!isTouchRef.current) {
            setOpen(prev => !prev);
          }
          setTimeout(() => { isTouchRef.current = false; }, 300);
        }}
        onMouseEnter={() => {
          if (!isTouchRef.current) setOpen(true);
        }}
        onMouseLeave={() => {
          if (!isTouchRef.current) setOpen(false);
        }}
        className="glossary-term"
        style={{
          // Definitions, not links: inherit the surrounding text color and
          // weight, and signal "hover for a definition" with a neutral dotted
          // underline. Hyperlinks stay teal with a solid underline, so the
          // two are visually distinct at a glance.
          borderBottom: "1.5px dotted #A8A29E",
          cursor: "help",
          color: "inherit",
          WebkitTapHighlightColor: "rgba(14, 95, 86, 0.1)",
          padding: "2px 0",
        }}
      >
        {text}
      </span>
      {open && pos && (
        <span style={{
          position: "fixed",
          ...(pos.showAbove
            ? { bottom: `${window.innerHeight - pos.top + 6}px` }
            : { top: `${pos.top + 24}px` }),
          left: pos.left,
          whiteSpace: "normal",
          // border-box matters here: the edge-clamping math above assumes the
          // rendered width equals this number. Without it the 14px horizontal
          // padding is added on top, making the box 28px wider than the clamp
          // allowed for, which pushed tooltips off the right edge on phones.
          boxSizing: "border-box",
          width: Math.min(260, window.innerWidth - 16), maxWidth: "calc(100vw - 16px)",
          background: "#1C1917", color: "#fff",
          fontSize: 13, lineHeight: 1.5, padding: "10px 14px",
          borderRadius: 8, zIndex: 10000,
          fontWeight: 400, fontFamily: THEME.sans,
          boxShadow: "0 4px 16px rgba(0,0,0,0.2)",
          pointerEvents: "none",
        }}>
          {tooltip}
        </span>
      )}
    </span>
  );
}

/* ─── GlossaryText ─── */
/* Splits text into plain spans and GlossaryTerm spans based on GLOSSARY patterns.
   Respects GlossaryScope: within a scope, each definition is only tooltipped once.
   If no scope is present, falls back to per-instance first-occurrence tracking. */

export function GlossaryText({ text }: { text: string }) {
  const scopeRef = useContext(GlossaryScopeCtx);
  // If no scope provided, create a local set so each instance still deduplicates
  // its own repeated terms (e.g. "clinical trial" appearing 5 times in one paragraph)
  const localRef = useRef<Set<string>>(new Set());
  const shown = scopeRef ?? localRef;

  // Track which definitions THIS instance claimed, so re-renders don't lose them.
  // Without this, parent re-renders (API data loading, state changes) would cause
  // all terms to appear in `shown` and render as plain text on the second pass.
  const claimedRef = useRef<Set<string>>(new Set());
  const prevTextRef = useRef<string>("");

  const parts = useMemo(() => {
    // If text changed, clear our claims (we'll reclaim below)
    if (text !== prevTextRef.current) {
      claimedRef.current.forEach(key => shown.current.delete(key));
      claimedRef.current = new Set();
      prevTextRef.current = text;
    }

    const result: { text: string; tooltip?: string }[] = [];
    let remaining = text;
    // Track first-occurrence WITHIN this single text block, separate from
    // the cross-render claimedRef. This ensures "cabozantinib" only gets
    // a tooltip on its first appearance even within one paragraph.
    const shownInThisText = new Set<string>();

    while (remaining.length > 0) {
      let earliest = { index: Infinity, length: 0, tooltip: "", key: "" };
      for (const [pattern, definition] of GLOSSARY) {
        pattern.lastIndex = 0;
        const match = pattern.exec(remaining);
        if (!match) continue;
        // Earliest match wins, and at the SAME position the LONGEST match wins.
        //
        // The length tiebreak is not cosmetic. GLOSSARY holds both broad terms
        // and the specific phrases built on them ("autosomal" and "autosomal
        // recessive", "complement" and "complement activation", "antibody" and
        // "antibody-drug conjugate"). Both patterns match at the same index, so
        // comparing on index alone let whichever sits earlier in the array win,
        // which was the broad one. That silently shadowed 47 curated
        // definitions: readers hovering "palliative care" got the definition of
        // "palliative", and the underline covered only the first word. Longest
        // match at a given position is the standard rule for exactly this, and
        // it makes the result depend on specificity rather than array order.
        const better =
          match.index < earliest.index ||
          (match.index === earliest.index && match[0].length > earliest.length);
        if (better) {
          earliest = { index: match.index, length: match[0].length, tooltip: definition, key: definition };
        }
      }
      if (earliest.index === Infinity) {
        result.push({ text: remaining });
        break;
      }
      if (earliest.index > 0) {
        result.push({ text: remaining.slice(0, earliest.index) });
      }

      // Three checks for whether to show the tooltip:
      // 1. Already shown earlier in THIS text block → plain
      // 2. Already shown by ANOTHER GlossaryText in the same scope → plain
      // 3. First occurrence anywhere → show tooltip
      const alreadyInThisText = shownInThisText.has(earliest.key);
      const alreadyShownByOther = shown.current.has(earliest.key) && !claimedRef.current.has(earliest.key);

      if (alreadyInThisText || alreadyShownByOther) {
        result.push({ text: remaining.slice(earliest.index, earliest.index + earliest.length) });
      } else {
        // First occurrence: render as tooltip, claim it in scope
        shown.current.add(earliest.key);
        claimedRef.current.add(earliest.key);
        shownInThisText.add(earliest.key);
        result.push({ text: remaining.slice(earliest.index, earliest.index + earliest.length), tooltip: earliest.tooltip });
      }

      remaining = remaining.slice(earliest.index + earliest.length);
    }

    return result;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [text]);

  return (
    <>
      {parts.map((p, i) =>
        p.tooltip ? (
          <GlossaryTerm key={i} text={p.text} tooltip={p.tooltip} />
        ) : (
          <span key={i}>{p.text}</span>
        )
      )}
    </>
  );
}

/* ─── GlossaryParagraphs ─── */
/* Splits text on \n\n and renders each chunk as its own <p> with glossary
   tooltips active. Long disease descriptions are much easier to scan when
   broken at natural topic shifts (cause, presentation, diagnosis) rather
   than crammed into a single block. If the text has no paragraph breaks,
   falls back to a single <p>. */

export function GlossaryParagraphs({
  text,
  style,
  paragraphStyle,
}: {
  text: string;
  style?: React.CSSProperties;
  paragraphStyle?: React.CSSProperties;
}) {
  if (!text) return null;
  const paras = text.split(/\n\n+/).map(s => s.trim()).filter(Boolean);
  if (paras.length <= 1 && !/\n- /.test(text)) {
    return (
      <p style={{ margin: 0, ...style, ...paragraphStyle }}>
        <GlossaryText text={text} />
      </p>
    );
  }
  // A block can end in a short list: lines that start with "- " become
  // bullets (October 21, 2026), so long overviews read in steps, not a wall.
  return (
    <div style={style}>
      {paras.map((p, i) => {
        const lines = p.split(/\n/);
        const lead = lines.filter((l) => !l.startsWith("- ")).join(" ").trim();
        const items = lines.filter((l) => l.startsWith("- ")).map((l) => l.slice(2).trim());
        const top = i === 0 ? 0 : 14;
        return (
          <div key={i} style={{ margin: `${top}px 0 0` }}>
            {lead && <p style={{ margin: 0, ...paragraphStyle }}><GlossaryText text={lead} /></p>}
            {items.length > 0 && (
              <ul style={{ margin: lead ? "6px 0 0" : 0, paddingLeft: 20, display: "grid", gap: 4, ...paragraphStyle }}>
                {items.map((it, k) => <li key={k}><GlossaryText text={it} /></li>)}
              </ul>
            )}
          </div>
        );
      })}
    </div>
  );
}
